import { Set } from 'immutable';
import Contact from '../../Models/Contact';
import Collection from '../../Models/Collection';
import type { ID, ReduxAction } from '../../types';
import Occasion from '@support/Occasion';

export default class ArchivedContacts extends Collection<Contact> {
  page: number;
  hasNext: boolean;

  constructor(props: any = {}) {
    super(props);
    // instead of offset, archive endpoint uses page for pagination
    this.page = props.page || 1;
    this.hasNext = props.hasNext;
  }

  get canLoadMore(): boolean {
    return this.hasNext;
  }
}

export type ArchiveState = ArchivedContacts;
const initialArchiveState = new ArchivedContacts();

const setFetchingStatusForBatch =
  ({ contactIDs, isFetching }: { contactIDs: Set<ID>; isFetching: boolean }) =>
  (contact: Contact, id: ID) => {
    if ((contactIDs || Set()).has(id)) {
      return contact.copyWith({ isFetching });
    } else {
      return contact;
    }
  };

// For lists where order matters, we may want to switch to a model where we just hide
// items in the UI while a "delete" (and, in this case, "restore") is in progress. Then
// on failure, remove the "hide" flag, and on success clear these items from the store.
export function archiveReducer(
  state: ArchiveState = initialArchiveState,
  { type, payload }: ReduxAction,
) {
  switch (type) {
    case 'FETCH_ALL_ARCHIVED_CONTACTS_REQUESTED':
      return state.copyWith({ isFetching: true });

    case 'FETCH_ALL_ARCHIVED_CONTACTS_SUCCEEDED':
      const archivedContacts = payload?.response?.results.map((archivedContact) => [
        String(archivedContact.id),
        new Contact(archivedContact),
      ]);

      if (archivedContacts) {
        return state.copyWith({
          isFetching: false,
          updated: new Occasion(),
          persistedItemsOnServer: payload.response.total,
          hasNext: payload.response.has_next,
          page: payload.page,
          items: payload.page == 1 ? archivedContacts : state.items.merge(archivedContacts),
        });
      } else {
        return state.copyWith({ isFetching: false });
      }

    case 'FETCH_ARCHIVED_CONTACT_SUCCEEDED':
      return state.copyWith({
        items: state.items.update(payload.contactID, new Contact(payload.response), (contact) =>
          contact.copyWith({ ...(payload.response || {}) }),
        ),
      });

    // case 'DELETE_ARCHIVED_CONTACT_BATCH_REQUESTED':
    case 'RESTORE_ARCHIVED_CONTACT_BATCH_REQUESTED':
      return state.copyWith({
        items: state.items.map(
          setFetchingStatusForBatch({ contactIDs: payload.contactIDs, isFetching: true }),
        ),
      });

    // case 'DELETE_ARCHIVED_CONTACT_BATCH_SUCCEEDED':
    case 'RESTORE_ARCHIVED_CONTACT_BATCH_SUCCEEDED':
      return state.copyWith({
        items: state.items.filter((_, id) => !(payload.contactIDs || Set()).has(id)),
      });

    // case 'DELETE_ARCHIVED_CONTACT_BATCH_FAILED':
    case 'RESTORE_ARCHIVED_CONTACT_BATCH_FAILED':
      return state.copyWith({
        items: state.items.map(
          setFetchingStatusForBatch({ contactIDs: payload.contactIDs, isFetching: false }),
        ),
      });

    default:
      return state;
  }
}
